/* eslint-disable */
import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { ChevronLeft } from "lucide-react";

const UPDATED = "June 7, 2026";

function LegalShell({ title, children }) {
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/" className="text-sm text-gray-600 hover:text-black inline-flex items-center gap-1.5">
            <ChevronLeft size={14} /> Home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
        <div className="overline mb-2">Legal</div>
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mb-2">{title}</h1>
        <p className="text-sm text-gray-500 mb-10">Last updated: {UPDATED}</p>
        <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed space-y-4">{children}</div>
      </main>
      <footer className="border-t border-gray-200 py-6 text-center text-xs text-gray-500">
        © {new Date().getFullYear()} Fair Rideshare LLC · <Link to="/privacy" className="hover:underline">Privacy</Link> · <Link to="/terms" className="hover:underline">Terms</Link>
      </footer>
    </div>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy">
      <h2 className="font-heading text-xl font-bold mt-6">1. Introduction</h2>
      <p>Fair Rideshare LLC ("we", "us") operates phillyrideshare.com and crm.phillyrideshare.com. This Privacy Policy explains how we collect, use, and protect your information when you book a vehicle, sign a rental agreement, or use our services.</p>

      <h2 className="font-heading text-xl font-bold mt-6">2. Information We Collect</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Identity & Contact:</strong> name, phone, email, mailing address</li>
        <li><strong>Driver verification:</strong> driver's license number, expiration, front and back photos, insurance card</li>
        <li><strong>Rental details:</strong> dates, vehicle selected, signature</li>
        <li><strong>Payment:</strong> amounts paid, method (Cash, Zelle, Cash App, Card)</li>
        <li><strong>Communications:</strong> SMS/email exchanges related to your rental</li>
        <li><strong>Device data:</strong> IP address, browser type, pages visited (cookies)</li>
      </ul>

      <h2 className="font-heading text-xl font-bold mt-6">3. How We Use Information</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>To process and manage your rental reservation</li>
        <li>To verify your eligibility to drive (license + insurance)</li>
        <li>To send booking confirmations, pickup/return reminders, and balance reminders by SMS or email</li>
        <li>To recover fees, tolls, citations, and damages</li>
        <li>To comply with legal obligations and respond to law enforcement</li>
      </ul>

      <h2 className="font-heading text-xl font-bold mt-6">4. SMS & Email Communications</h2>
      <p>By providing your phone number and email at booking, you consent to receive transactional messages from Fair Rideshare LLC (booking confirmations, pickup/return reminders, balance reminders, toll notices, agreement signing links). Message and data rates may apply. Reply <strong>STOP</strong> to opt out at any time. We do not share your contact details with third-party marketers.</p>

      <h2 className="font-heading text-xl font-bold mt-6">5. Information Sharing</h2>
      <p>We do not sell your personal data. We share information only with:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Service providers (SMS / hosting / payment processors) bound by confidentiality</li>
        <li>Toll authorities, courts, or law enforcement when violations are transferred or legally required</li>
        <li>Insurance providers (rentalcover.com) as needed for your coverage</li>
      </ul>

      <h2 className="font-heading text-xl font-bold mt-6">6. Data Security</h2>
      <p>We use industry-standard encryption (HTTPS / TLS) for all data in transit. Driver's license images and signatures are stored in secure, access-controlled storage. We never store full payment-card numbers on our servers — payment processing is handled by PCI-compliant third parties.</p>

      <h2 className="font-heading text-xl font-bold mt-6">7. Data Retention</h2>
      <p>We retain rental records, agreements, and supporting documents for at least 7 years to comply with tax and legal obligations. You may request earlier deletion of non-essential records by contacting us.</p>

      <h2 className="font-heading text-xl font-bold mt-6">8. Your Rights</h2>
      <p>You may request access to, correction of, or deletion of your personal data by emailing or texting us at (215) 770-0679. We will respond within 30 days.</p>

      <h2 className="font-heading text-xl font-bold mt-6">9. Children</h2>
      <p>Our services are intended for adults age 21 or older. We do not knowingly collect data from minors.</p>

      <h2 className="font-heading text-xl font-bold mt-6">10. Changes</h2>
      <p>We may update this policy. The "Last updated" date at the top reflects the current version. Continued use after changes constitutes acceptance.</p>

      <h2 className="font-heading text-xl font-bold mt-6">11. Contact</h2>
      <p>Fair Rideshare LLC · Philadelphia, PA · (215) 770-0679</p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of Service">
      <h2 className="font-heading text-xl font-bold mt-6">1. Acceptance</h2>
      <p>By accessing phillyrideshare.com, crm.phillyrideshare.com, or booking a vehicle from Fair Rideshare LLC ("we", "us", "Company"), you ("Renter") agree to these Terms of Service in addition to the signed Vehicle Rental Agreement.</p>

      <h2 className="font-heading text-xl font-bold mt-6">2. Eligibility</h2>
      <p>You must be at least 21 years old, hold a valid driver's license, and maintain valid auto insurance covering the rental period.</p>

      <h2 className="font-heading text-xl font-bold mt-6">3. Bookings & Payments</h2>
      <p>Booking is confirmed when full payment of the weekly rate and security deposit is received. Rentals are weekly. Deposits are refundable within 7 days of return provided there are no damages, unpaid fees, or violations.</p>

      <h2 className="font-heading text-xl font-bold mt-6">4. Cancellations</h2>
      <p>You may cancel up to 24 hours before pickup for a full refund. Cancellations within 24 hours forfeit the deposit. No-shows are charged the first week in full.</p>

      <h2 className="font-heading text-xl font-bold mt-6">5. Renter Responsibility</h2>
      <p>You are fully responsible for fuel, tolls, parking fees, tickets, citations, repair costs from negligence, and all damages while the vehicle is in your possession. You are not covered by any insurance policy of Fair Rideshare LLC and may not file claims against the Company. Insurance must be obtained independently via <a href="https://www.rentalcover.com" className="text-amber-700 underline">rentalcover.com</a> or your own carrier.</p>

      <h2 className="font-heading text-xl font-bold mt-6">6. Prohibited Use</h2>
      <p>You may not use the vehicle for racing, towing, illegal activities, ride-sharing platforms not approved by us, or by unauthorized drivers.</p>

      <h2 className="font-heading text-xl font-bold mt-6">7. Late Returns</h2>
      <p>Vehicles returned after the agreed return time are subject to additional daily charges and may be reported as overdue. Vehicles unreturned 24 hours past due may be recovered using GPS tracking.</p>

      <h2 className="font-heading text-xl font-bold mt-6">8. SMS & Electronic Communications</h2>
      <p>You consent to receive transactional SMS and email messages related to your rental. Reply STOP to opt out at any time.</p>

      <h2 className="font-heading text-xl font-bold mt-6">9. Electronic Signature</h2>
      <p>You consent that your electronic signature on the rental agreement (drawn or typed) is legally binding under the E-SIGN Act and UETA.</p>

      <h2 className="font-heading text-xl font-bold mt-6">10. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law, Fair Rideshare LLC's total liability shall not exceed the rental fees paid by the Renter for the rental in question.</p>

      <h2 className="font-heading text-xl font-bold mt-6">11. Governing Law</h2>
      <p>These Terms are governed by the laws of the Commonwealth of Pennsylvania. Any disputes shall be resolved in the courts of Philadelphia County, PA.</p>

      <h2 className="font-heading text-xl font-bold mt-6">12. Contact</h2>
      <p>Fair Rideshare LLC · Philadelphia, PA · (215) 770-0679</p>
    </LegalShell>
  );
}
